'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import './style.scss';
import CarbonPagination from '@/components/Pagination/Pagination';
import ListCardComponent from '@/components/Cards/ListCardComponent/listCardComponent';
import CarbonAccordion from '@/components/Accordion/Accordion';
import SortDropdown, { SortOrder } from '@/components/SortDropdown/SortDropdown';
import { Checkbox } from '@carbon/react';
import Loading from '@/components/Loader';
import { getAISearchResults, SearchGroupResult, SearchResponseData } from '@/apis/SearchAPIs';

import { thisHostAndBasePath } from '@/../config';
import { cleanMetadata } from '@/utils/cleanMetadata';
import ContentEngagementClient from '@/analytics/ContentEngagementClient';

interface FilterOption {
  label: string;
  value: string;
  count: number;
  checked: boolean;
}

interface SearchResult {
  slug: string;
  title: string;
  link: string;
  excerpt: string;
  date: string;
  contentType: string;
  content_type: string;
  primaryCategory: string;
  taxonomies: any;
  score?: number;
  chunk_count?: number;
}

const SORT_MAP: Record<SortOrder, string> = {
  newest: 'newest',
  oldest: 'oldest',
  title_ascending: 'title_asc',
  title_descending: 'title_desc',
};

export default function Search() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryFromUrl = searchParams.get('q') || '';

  const [searchQuery, setSearchQuery] = useState(queryFromUrl);
  const [submittedQuery, setSubmittedQuery] = useState(queryFromUrl);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<SortOrder>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [contentTypeFilters, setContentTypeFilters] = useState<FilterOption[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const selectedContentTypes = useMemo(
    () => contentTypeFilters.filter(filter => filter.checked).map(filter => filter.value),
    [contentTypeFilters],
  );

  const selectedContentType = selectedContentTypes[0] || '';

  const fetchSearchResults = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const sortParam = SORT_MAP[sortBy];
      const data: SearchResponseData | null = await getAISearchResults({
        query: submittedQuery,
        page: currentPage,
        limit: itemsPerPage,
        sort_by: sortParam,
        content_type: selectedContentType || undefined,
      });

      if (!data) {
        throw new Error('Failed to fetch search results. Please try again.');
      }

      const transformedResults: SearchResult[] = [];
      const responseItems = Array.isArray(data.groups)
        ? data.groups
        : Array.isArray(data.results)
          ? data.results
          : [];
      responseItems.forEach((item: SearchGroupResult) => {
        transformedResults.push({
          slug: item.slug,
          title: item.title,
          link: `/${item.content_uri}`,
          excerpt: item.subtitle || '',
          date: item.publish_date,
          contentType: item.content_type,
          content_type: item.content_type,
          primaryCategory: '',
          taxonomies: {},
          score: item.score,
          chunk_count: item.chunk_count,
        });
      });

      const totalCount = data.total || 0;

      switch (sortBy) {
        case 'newest':
          transformedResults.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );
          break;
        case 'oldest':
          transformedResults.sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
          );
          break;
        case 'title_ascending':
          transformedResults.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'title_descending':
          transformedResults.sort((a, b) => b.title.localeCompare(a.title));
          break;
        default:
          transformedResults.sort((a, b) => (b.score || 0) - (a.score || 0));
      }

      setSearchResults(transformedResults);
      setTotalResults(totalCount);

      if (isInitialLoad && data.type_counts) {
        const filters: FilterOption[] = Object.entries(data.type_counts).map(
          ([contentType, count]) => {
            const label = contentType
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            return {
              label,
              value: contentType,
              count: count as number,
              checked: false,
            };
          },
        );

        filters.sort((a, b) => b.count - a.count);

        setContentTypeFilters(filters);
        setIsInitialLoad(false);
      }
    } catch (err: any) {
      console.error('Search API error:', err);
      setError(err.message || 'Failed to fetch search results. Please try again.');
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [submittedQuery, currentPage, itemsPerPage, sortBy, selectedContentType, isInitialLoad]);

  useEffect(() => {
    setSearchQuery(queryFromUrl);
    setSubmittedQuery(queryFromUrl);
    setCurrentPage(1);
  }, [queryFromUrl]);

  useEffect(() => {
    if (!submittedQuery.trim()) {
      setSearchResults([]);
      setTotalResults(0);
      setLoading(false);
      return;
    }

    fetchSearchResults();
  }, [fetchSearchResults, submittedQuery]);

  const handleFilterChange = (index: number) => {
    setCurrentPage(1);
    setContentTypeFilters(prevFilters =>
      prevFilters.map((filter, filterIndex) => ({
        ...filter,
        checked: filterIndex === index ? !filter.checked : false,
      })),
    );
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) {
      setSearchResults([]);
      setTotalResults(0);
      setSubmittedQuery('');
      setContentTypeFilters([]);
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set('q', trimmedQuery);
    params.delete('page');

    // Reset filters and pagination for new search
    setSubmittedQuery(trimmedQuery);
    setCurrentPage(1);
    setContentTypeFilters(prevFilters =>
      prevFilters.map(filter => ({ ...filter, checked: false })),
    );
    setIsInitialLoad(true);

    router.push(`/search${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const filterItems = [
    {
      key: 'content-type',
      title: 'Content Type',
      openByDefault: true,
      content: contentTypeFilters.map((option, index) => (
        <Checkbox
          key={option.label}
          id={`content-type-${option.label}`}
          labelText={`${option.label} (${option.count})`}
          checked={option.checked}
          onChange={() => handleFilterChange(index)}
        />
      )),
    },
  ];

  const pageMetaData = useMemo(
    () =>
      cleanMetadata({
        slug: '/search/',
        publish_date: null,
        date_modified: null,
        url: `${thisHostAndBasePath}` + `/search`,
        title: 'Search Page',
        favourite: '',
        like: '',
        follow: '',
        status: '',
        primary_category: '',
        tags: '',
        strategic_content_area: '',
        authors: '',
        authors_email: '',
        editors: '',
        editors_email: '',
        record_type: 'search-page',
        secondary_tag: '',
      }),
    [],
  );

  return (
    <>
      <ContentEngagementClient metaData={pageMetaData} />
      <div className="search-custom-page">
        <div className="search-bar-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              className="search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search..."
            />
            <button type="submit" className="search-button" aria-label="Search">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 18L12.65 12.65"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </form>
        </div>

        <div className="search-content">
          <aside className="search-sidebar">
            <h2 className="filter-title">Filter by</h2>
            <CarbonAccordion items={filterItems} />
          </aside>

          <main className="search-results">
            <div className="results-header">
              <div className="results-info">
                {error ? (
                  <p className="error-message">{error}</p>
                ) : (
                  !loading && (
                    <>
                      <p className="results-count">
                        Found {totalResults} search results in IBM Developer.
                      </p>
                      <a href="#" className="search-all-link">
                        Search for "{searchQuery}" in All of IBM.com instead?
                      </a>
                    </>
                  )
                )}
              </div>
            </div>

            {loading ? (
              <Loading />
            ) : !error ? (
              <CarbonPagination
                key={`${searchQuery}-${selectedContentType}-${currentPage}-${itemsPerPage}`}
                data={searchResults}
                totalItems={totalResults}
                pageSize={itemsPerPage}
                pageSizeOptions={[12, 24, 36]}
                currentPage={currentPage}
                renderItem={(item: any) => (
                  <ListCardComponent
                    key={`${item.content_type}-${item.slug}`}
                    title={item.title}
                    link={item.link}
                    displayUrl={item.link}
                    excerpt={item.excerpt}
                    contentType={item.contentType}
                    info={false}
                  />
                )}
                listClassName="results-list"
                onPageChange={page => setCurrentPage(page)}
                onPageSizeChange={newPageSize => setItemsPerPage(newPageSize)}
                emptyMessage="No search results found. Try adjusting your search query or filters."
              />
            ) : null}
          </main>

          <aside className="search-controls-sidebar">
            <SortDropdown
              sortOrder={sortBy}
              onChange={newOrder => setSortBy(newOrder)}
              labelText="Sort by"
              className="sort-dropdown-container"
            />
          </aside>
        </div>
      </div>
    </>
  );
}
